import { logEvent } from '../analytics/firAnalytics'
const MAX_RETRIES = 2
const RETRY_DELAY = 1000

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = 5000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error('IndexedDB operation timed out')), timeoutMs)
    )
  ])
}

/**
 * Executes an asynchronous function with retry logic
 * @param asyncOperation The asynchronous function to execute
 * @param errorContext Context for error logging
 * @returns The result of the operation or undefined if all retries fail
 */
export const executeWithRetry = async <T>(
  asyncOperation: () => Promise<T>,
  errorContext: string
): Promise<T | null> => {
  let retries = 0
  while (retries < MAX_RETRIES) {
    try {
      return await asyncOperation()
    } catch (e) {
      retries++
      if (retries === MAX_RETRIES) {
        console.error(
          '[Retry] Failed operation after multiple retries:',
          errorContext,
          e
        )
        logEvent(`max_failed_fetch_reached`, {
          error: e,
          context: errorContext,
        })
        return null
      }
      console.warn(
        '[Retry] Operation failed, retrying:',
        errorContext,
        `Attempt ${retries}/${MAX_RETRIES}`
      )
      await wait(RETRY_DELAY * retries)
    }
  }
  return null
}
/**
 * Checks if the browser navigator is currently online.
 */
export const isNavigatorOnline = (): boolean => {
  if (typeof window !== 'undefined' && typeof window.navigator !== 'undefined') {
    return window.navigator.onLine
  }
  return true
}